import { Dispatch, SetStateAction, memo } from 'react';
import { Box, withStyles } from '@material-ui/core';
import {
  SButton,
  SEmptyState,
  SSearchInput,
  SText
} from '@setvi/shared/components';
import { FormattedFacet, sortFullList } from 'Hooks/useAlgoliaProducts';
import { transformAlgoliaFacetLabel } from 'pages/products/utils';
import { useSingerLabel } from 'hooksv2';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { Category } from 'Services/Mongo/Types';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { distances } from '@setvi/shared/styles';
import { StandardList } from './components/standard-list';
import { useFacet } from './useFacet';
import { ModifiedFacet, optionsLimit } from '..';
import { SearchLoader } from '../loader';
import { CertificationList } from './components/certifications-list';
import { Container, FacetButton, FacetContentContainer } from './styled';

interface Content extends Omit<
  CustomRefinmentListProps,
  | 'globalSearch'
  | 'attributesForFaceting'
  | 'selectedCategory'
  | 'modifiedFacets'
  | 'setModifiedFacets'
  | 'expandedAccordion'
  | 'handleExpandAccordion'
> {
  searchQuery: string;
}

const Button = withStyles(({ spacing }) => ({
  root: {
    padding: spacing(1),
    minHeight: 'auto',
    border: 'none',
    '&.MuiButton-outlinedPrimary:hover': {
      border: 'none'
    }
  }
}))(SButton);

const Content = ({
  searchQuery,
  selectedFacets,
  facet,
  onSelectFacet
}: Content) => {
  if (facet?.values?.length === 0 || !facet?.values)
    return (
      <Box my={2}>
        <SEmptyState
          imageSrc="/images/no-results.svg"
          subTitle="No Results Found"
        />
      </Box>
    );

  return (
    <>
      {facet.values.map(({ value, count }) => {
        const selectedFacetsValues = selectedFacets
          .filter(i => i.includes(facet.name))
          .map(i => i.replace(`${facet.name}:`, ''));

        const isChecked = selectedFacetsValues.includes(value);

        if (facet.name === 'custom.product_certifications.value') {
          return (
            <CertificationList
              key={value}
              searchQuery={searchQuery}
              item={value}
              count={count}
              isChecked={isChecked}
              onChange={e => {
                onSelectFacet(e.target.checked, [`${facet.name}:${value}`]);
              }}
            />
          );
        }

        return (
          <StandardList
            key={value}
            searchQuery={searchQuery}
            item={value}
            count={count}
            isChecked={isChecked}
            onChange={e => {
              onSelectFacet(e.target.checked, [`${facet.name}:${value}`]);
            }}
          />
        );
      })}
    </>
  );
};

export interface CustomRefinmentListProps {
  facet: FormattedFacet;
  attributesForFaceting: readonly string[];
  selectedFacets: string[];
  selectedCategory: Category;
  globalSearch: string;
  modifiedFacets: ModifiedFacet[];
  expandedAccordion: boolean;
  handleExpandAccordion: (name: string) => void;
  onSelectFacet: (isChecked: boolean, facets: string[]) => void;
  setModifiedFacets: Dispatch<SetStateAction<ModifiedFacet[]>>;
}

export const CustomRefinmentList = memo(
  ({
    facet: initFacet,
    attributesForFaceting,
    selectedFacets,
    selectedCategory,
    globalSearch,
    modifiedFacets,
    expandedAccordion,
    handleExpandAccordion,
    onSelectFacet,
    setModifiedFacets
  }: CustomRefinmentListProps) => {
    const { search, handleSearch, searchResult, setSearch, loading } = useFacet(
      {
        selectedFacets,
        selectedCategory,
        globalSearch,
        setModifiedFacets
      }
    );
    const { getLabel: getSingerLabel } = useSingerLabel();
    const { productConfigurations } = useAppContext();
    const expanded = modifiedFacets?.find(
      i => i.name === initFacet.name
    )?.expanded;

    const facet: FormattedFacet = search.trim().length
      ? searchResult
      : initFacet;

    const showSearch: boolean =
      attributesForFaceting
        ?.find(attribute => attribute.includes(facet?.name))
        ?.includes('searchable') || false;

    const facetTitle = facet?.name && transformAlgoliaFacetLabel(facet.name);
    const displayFacetTitle =
      productConfigurations?.labels?.[facet?.name] ||
      (facetTitle && getSingerLabel(facetTitle));

    return (
      <Container>
        <FacetButton
          onClick={() => handleExpandAccordion(facet.name)}
          style={{ marginBottom: expandedAccordion ? 4 : 0 }}>
          <SText
            size="sm"
            lineHeight="20px"
            title={displayFacetTitle || facet?.name}
            weight="bold"
          />
          <ExpandMoreIcon
            style={{
              transform: `rotate(${expandedAccordion ? '-180deg' : '0'})`
            }}
          />
        </FacetButton>
        {expandedAccordion && (
          <FacetContentContainer>
            {showSearch && (
              <SSearchInput
                width="100%"
                height={40}
                searchIconSize="14px"
                style={{
                  height: 32,
                  backgroundColor: '#F8F9FA',
                  margin: `8px 0 ${distances.px.base} 0`,
                  borderRadius: 99
                }}
                onChange={val => handleSearch(val, facet.name)}
                searchedValue={search}
              />
            )}
            <Box overflow="auto">
              {loading ? (
                <SearchLoader />
              ) : (
                <Content
                  facet={{
                    ...facet,
                    values: sortFullList(
                      facet.name,
                      expanded
                        ? facet.values
                        : facet.values.slice(0, optionsLimit),
                      selectedFacets
                    )
                  }}
                  searchQuery={search}
                  selectedFacets={selectedFacets}
                  onSelectFacet={(isChecked: boolean, facets: string[]) => {
                    setSearch('');
                    onSelectFacet(isChecked, facets);
                  }}
                />
              )}
            </Box>
            {facet.values.length > optionsLimit && (
              <Box mb={distances.px.base}>
                <Button
                  variant="outlined"
                  style={{
                    fontSize: 14,
                    lineHeight: '20px',
                    padding: `${distances.px.xsmall} ${distances.px.base}`
                  }}
                  endIcon={
                    <ExpandMoreIcon
                      style={{
                        transform: `rotate(${expanded ? '-180deg' : '0'})`
                      }}
                    />
                  }
                  onClick={() =>
                    setModifiedFacets(prev =>
                      prev.map(i =>
                        i.name === initFacet.name
                          ? { ...i, expanded: !i.expanded }
                          : i
                      )
                    )
                  }>
                  {expanded
                    ? 'View Less'
                    : `View More (${facet.values.length - optionsLimit})`}
                </Button>
              </Box>
            )}
          </FacetContentContainer>
        )}
      </Container>
    );
  }
);
